import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import GoogleMapKey from "../../../../pages/Utils/googleMapKey";
import { FaMotorcycle, FaUserClock, FaUserCheck } from "react-icons/fa";
import ReactDOMServer from "react-dom/server";
import locationImage from "../../../../assest/images/location.png";

const containerStyle = {
  width: "100%",
  height: "450px",
};

const COLORS = [
  "#007BFF",
  "#FF5733",
  "#28A745",
  "#FFC107",
  "#6F42C1",
  "#20C997",
  "#E83E8C",
  "#FD7E14",
  "#17A2B8",
  "#6610F2",
  "#FF6F61",
  "#4CAF50",
  "#9C27B0",
  "#03A9F4",
  "#FF9800",
  "#8BC34A",
  "#795548",
  "#009688",
  "#F44336",
  "#607D8B",
];

const lightenColor = (hex, percent) => {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
};

const getCoordinate = (latitude, longitude) => {
  if (
    (typeof latitude === "string" && !latitude.trim()) ||
    (typeof longitude === "string" && !longitude.trim())
  ) {
    return null;
  }

  const lat = Number(latitude);
  const lng = Number(longitude);

  if (
    latitude === null ||
    latitude === undefined ||
    longitude === null ||
    longitude === undefined ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    return null;
  }

  return { lat, lng };
};

const getCheckinLocations = (record) => {
  const locations = [];
  const checkinLocation = getCoordinate(
    record?.checkinLocationLat,
    record?.checkinLocationLng,
  );
  const deliveryLocation = getCoordinate(record?.latitude, record?.longitude);

  if (checkinLocation) {
    locations.push({ position: checkinLocation, type: "Check-in" });
  }
  if (
    deliveryLocation &&
    (!checkinLocation ||
      deliveryLocation.lat !== checkinLocation.lat ||
      deliveryLocation.lng !== checkinLocation.lng)
  ) {
    locations.push({ position: deliveryLocation, type: "Delivery" });
  }

  return locations;
};

const getCheckinMarkers = (checkinData) => {
  const markersByPosition = new Map();

  checkinData.forEach((record) => {
    getCheckinLocations(record).forEach(({ position, type }) => {
      const key = `${position.lat},${position.lng}`;
      const marker = markersByPosition.get(key) || {
        position,
        records: [],
      };
      marker.records.push({ ...record, markerType: type });
      markersByPosition.set(key, marker);
    });
  });

  return Array.from(markersByPosition.values());
};

const AgentsMapComponents = ({ deliveryData = [], checkinData = [] }) => {
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [colorMap, setColorMap] = useState({});
  const mapRef = useRef(null);
  const [mapCenter] = useState({ lat: 26.0667, lng: 50.5577 });

  const checkinMarkers = useMemo(
    () => getCheckinMarkers(checkinData),
    [checkinData],
  );

  useEffect(() => {
    if (!deliveryData?.length) return;

    setColorMap((prevMap) => {
      const newMap = { ...prevMap };
      let colorIndex = Object.keys(prevMap).length;

      deliveryData.forEach((agent) => {
        if (!newMap[agent.agentId]) {
          newMap[agent.agentId] = COLORS[colorIndex % COLORS.length];
          colorIndex++;
        }
      });

      return newMap;
    });
  }, [deliveryData]);

  useEffect(() => {
    if (
      !mapLoaded ||
      !checkinMarkers.length ||
      !window.google?.maps?.LatLngBounds
    ) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    checkinMarkers.forEach(({ position }) => {
      bounds.extend(position);
    });

    if (checkinMarkers.length === 1) {
      mapRef.current.setCenter(bounds.getCenter());
      mapRef.current.setZoom(15);
    } else if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds, 64);
    }
  }, [checkinMarkers, mapLoaded]);

  const getAgentIcon = (color) => {
    if (!mapLoaded || !window.google?.maps?.Size) return null;
    const svgString = ReactDOMServer.renderToString(
      <FaMotorcycle color={color} size={36} />
    );
    const svgBase64 = `data:image/svg+xml;base64,${btoa(svgString)}`;
    return {
      url: svgBase64,
      scaledSize: new window.google.maps.Size(55, 55),
      anchor: new window.google.maps.Point(27, 27),
    };
  };

  const getCustomerIcon = (status, color) => {
    if (!window.google?.maps?.Size) return null;
    const icon =
      status === "Delivered" ? (
        <FaUserCheck color={color} size={20} />
      ) : (
        <FaUserClock color={color} size={20} />
      );
    const svgString = ReactDOMServer.renderToString(icon);
    const svgBase64 = `data:image/svg+xml;base64,${btoa(svgString)}`;
    return {
      url: svgBase64,
      scaledSize: new window.google.maps.Size(32, 32),
      anchor: new window.google.maps.Point(16, 16),
    };
  };

  const getDeliveryMarkerIcon = (label, { color = "#16a34a", dotColor = "#22c55e", pinColor = "#15803d", pinGradTop = "#22c55e", type = "agent" } = {}) => {
    if (!mapLoaded || !window.google?.maps?.Size) return null;

    const rawName = label || (type === "driver" ? "Driver" : type === "customer" ? "Customer" : "Agent");
    const displayName = rawName.length > 20 ? rawName.slice(0, 18) + "…" : rawName;
    const escapedName = displayName
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");

    const textLen = displayName.length;
    const pillWidth = Math.max(76, Math.round(textLen * 7.4 + 28));
    const totalWidth = pillWidth + 16;
    const totalHeight = 58;
    const pinX = Math.round(totalWidth / 2);
    const pillX = Math.round((totalWidth - pillWidth) / 2);
    const uid = `${pinX}-${type}`;

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${totalHeight}" viewBox="0 0 ${totalWidth} ${totalHeight}">
    <defs>
      <filter id="pill-shadow-${uid}" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#0f172a" flood-opacity="0.18" />
      </filter>
      <filter id="pin-shadow-${uid}" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="2" stdDeviation="1.5" flood-color="${pinColor}" flood-opacity="0.28" />
      </filter>
      <linearGradient id="pin-grad-${uid}" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="${pinGradTop}" />
        <stop offset="100%" stop-color="${pinColor}" />
      </linearGradient>
    </defs>

    <!-- Floating Name Pill -->
    <g filter="url(#pill-shadow-${uid})">
      <rect x="${pillX}" y="2" width="${pillWidth}" height="24" rx="12" fill="#ffffff" stroke="#cbd5e1" stroke-width="1.2" />
      <circle cx="${pillX + 11}" cy="14" r="3.5" fill="${dotColor}" />
      <circle cx="${pillX + 11}" cy="14" r="5.5" fill="${dotColor}" opacity="0.35" />
      <text x="${pillX + 21}" y="17.5" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" font-size="11" font-weight="700" fill="#0f172a">${escapedName}</text>
    </g>

    <!-- Location Pin Stem & Head -->
    <g filter="url(#pin-shadow-${uid})">
      <path d="M ${pinX - 6} 38 L ${pinX} 53 L ${pinX + 6} 38 Z" fill="${pinColor}" />
      <circle cx="${pinX}" cy="38" r="8.5" fill="url(#pin-grad-${uid})" stroke="#ffffff" stroke-width="2" />
      <circle cx="${pinX}" cy="38" r="3" fill="#ffffff" />
    </g>
  </svg>`;

    const encoded = "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
    return {
      url: encoded,
      scaledSize: new window.google.maps.Size(totalWidth, totalHeight),
      anchor: new window.google.maps.Point(pinX, 53),
    };
  };

  const onLoad = (map) => {
    mapRef.current = map;
    setMapLoaded(true);
  };

  return (
    <div>
      <LoadScript googleMapsApiKey={GoogleMapKey}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={10}
          onLoad={onLoad}
        >
          {deliveryData?.map((agent) => {
            const agentColor = colorMap[agent?.agentId] || "#16a34a";
            const customerColor = lightenColor(agentColor, 10);
            const agentIcon = getDeliveryMarkerIcon(agent?.agentName, {
              color: agentColor,
              dotColor: agentColor,
              type: "agent",
            });

            return (
              <React.Fragment key={agent?.agentId}>
                <Marker
                  position={agent?.currentLocation}
                  icon={agentIcon || undefined}
                  title={agent?.agentName}
                  onClick={() => setSelectedMarker(agent)}
                />

                {agent?.customers?.map((customer) => {
                  const customerIcon = getDeliveryMarkerIcon(customer?.name, {
                    color: customerColor,
                    dotColor: customerColor,
                    type: "customer",
                  });
                  return (
                    <Marker
                      key={`${agent.agentId}-${customer?.id || customer?.customerId || Math.random()}`}
                      position={{ lat: customer.lat, lng: customer.lng }}
                      icon={customerIcon || undefined}
                      title={customer?.name}
                      onClick={() =>
                        setSelectedMarker({
                          ...customer,
                          agentName: agent?.agentName,
                        })
                      }
                    />
                  );
                })}
              </React.Fragment>
            );
          })}

          {checkinMarkers.map(({ position, records }) => {
            const markerNames = records
              .map(
                (record) =>
                  `${record?.employeeName || "Delivery person"}${
                    record?.employeeCode ? ` (${record.employeeCode})` : ""
                  }`,
              )
              .filter((name, index, names) => names.indexOf(name) === index);

            const labelText =
              markerNames.length > 1
                ? `${markerNames.length} Delivery Persons`
                : markerNames[0] || "Delivery Agent";

            const checkinIcon = getDeliveryMarkerIcon(labelText, {
              color: "#16a34a",
              dotColor: "#22c55e",
              type: "driver",
            });

            return (
              <Marker
                key={`${position.lat},${position.lng}`}
                position={position}
                icon={checkinIcon || undefined}
                title={markerNames.join(", ")}
                onClick={() =>
                  setSelectedMarker({
                    markerPosition: position,
                    records,
                    isCheckinRecord: true,
                  })
                }
              />
            );
          })}

          {selectedMarker && (
            <InfoWindow
              position={
                selectedMarker?.markerPosition
                  ? selectedMarker.markerPosition
                  : selectedMarker?.currentLocation
                  ? selectedMarker?.currentLocation
                  : { lat: selectedMarker?.lat, lng: selectedMarker?.lng }
              }
              onCloseClick={() => setSelectedMarker(null)}
              options={{
                pixelOffset: window.google
                  ? new window.google.maps.Size(0, -42)
                  : undefined,
              }}
            >
              <div
                style={{
                  minWidth: "220px",
                  maxWidth: "280px",
                  padding: "4px 2px",
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  color: "#0f172a",
                }}
              >
                {selectedMarker.isCheckinRecord ? (
                  selectedMarker.records?.map((record, index) => (
                    <div
                      key={`${record?.id || index}-${record?.markerType}`}
                      style={{
                        paddingBottom:
                          index < selectedMarker.records.length - 1 ? "10px" : "0",
                        marginBottom:
                          index < selectedMarker.records.length - 1 ? "10px" : "0",
                        borderBottom:
                          index < selectedMarker.records.length - 1
                            ? "1px solid #f1f5f9"
                            : "none",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <div
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "13px",
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                          }}
                        >
                          {record?.employeeName?.charAt(0) || "D"}
                        </div>
                        <div>
                          <strong
                            style={{
                              fontSize: "13px",
                              fontWeight: 700,
                              color: "#0f172a",
                              display: "block",
                            }}
                          >
                            {record?.employeeName || "Delivery Person"}
                          </strong>
                          <span
                            style={{
                              fontSize: "11px",
                              color: "#64748b",
                            }}
                          >
                            {record?.role || "Driver"} • Code: {record?.employeeCode || "N/A"}
                          </span>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                          fontSize: "11.5px",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ color: "#64748b" }}>Location Type:</span>
                          <span
                            style={{
                              fontWeight: 700,
                              color: "#16a34a",
                              background: "#f0fdf4",
                              padding: "1px 6px",
                              borderRadius: "4px",
                            }}
                          >
                            {record?.markerType || "Check-in"}
                          </span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ color: "#64748b" }}>Check-in Time:</span>
                          <span style={{ fontWeight: 600, color: "#1e293b" }}>
                            {record?.checkInTime || "N/A"}
                          </span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ color: "#64748b" }}>Visit Date:</span>
                          <span style={{ fontWeight: 600, color: "#1e293b" }}>
                            {record?.visitDate || "N/A"}
                          </span>
                        </div>
                        {record?.deliveryNo && (
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span style={{ color: "#64748b" }}>Delivery No:</span>
                            <span style={{ fontWeight: 600, color: "#1e293b" }}>
                              {record?.deliveryNo}
                            </span>
                          </div>
                        )}
                        {record?.customerName && (
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span style={{ color: "#64748b" }}>Customer:</span>
                            <span style={{ fontWeight: 600, color: "#1e293b" }}>
                              {record?.customerName}
                            </span>
                          </div>
                        )}
                        {record?.deliveryStatus && (
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span style={{ color: "#64748b" }}>Status:</span>
                            <span style={{ fontWeight: 700, color: "#1e293b" }}>
                              {record?.deliveryStatus}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : selectedMarker.agentPhone ? (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "6px",
                      }}
                    >
                      <div
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "13px",
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        {selectedMarker?.agentName?.charAt(0) || "A"}
                      </div>
                      <div>
                        <strong
                          style={{
                            fontSize: "13px",
                            fontWeight: 700,
                            color: "#0f172a",
                            display: "block",
                          }}
                        >
                          {selectedMarker?.agentName}
                        </strong>
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#64748b",
                          }}
                        >
                          Delivery Agent
                        </span>
                      </div>
                    </div>
                    <p style={{ margin: "6px 0 0", fontSize: "11.5px", color: "#475569" }}>
                      <strong>Phone:</strong> {selectedMarker?.agentPhone}
                    </p>
                  </div>
                ) : (
                  <div>
                    <strong
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#0f172a",
                        display: "block",
                      }}
                    >
                      {selectedMarker?.name}
                    </strong>
                    <span style={{ fontSize: "11px", color: "#64748b" }}>
                      Customer • Agent: {selectedMarker?.agentName}
                    </span>
                  </div>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>
    </div>
  );
};

export default AgentsMapComponents;
